import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Plus, Trash2, ChevronDown, ChevronUp, Loader2 } from "lucide-react";

/**
 * Phase UX-6.1 — Structured completion form for client portals.
 *
 * Renders matter-type-specific sections of fields (fetched from
 * `GET /api/client/portal/:token/completion`) and lets the client save
 * updates with `PUT`. Highlights missing items per section and surfaces
 * an overall "Ready for drafting" pill.
 */
export default function CompletionForm({ token, onChanged }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [openId,  setOpenId]  = useState(null);
  const [draft,   setDraft]   = useState({});           // {section_id: {field_id: value}}
  const [benefRows, setBenefRows] = useState([]);       // editable beneficiary rows

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/client/portal/${token}/completion`);
        if (cancelled) return;
        setData(data);
        // Seed beneficiary rows from server-side items if present.
        const ben = (data.sections || []).find((s) => s.id === "beneficiaries");
        if (ben?.list_field && Array.isArray(ben.items)) {
          setBenefRows(ben.items.length ? ben.items : [emptyRow()]);
        }
        // Auto-expand the first incomplete section.
        const firstIncomplete = (data.sections || []).find((s) => !s.complete);
        setOpenId(firstIncomplete ? firstIncomplete.id : null);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const setField = (sectionId, fieldId, value) => {
    setDraft((prev) => ({
      ...prev,
      [sectionId]: { ...(prev[sectionId] || {}), [fieldId]: value },
    }));
  };

  const benefAdd    = () => setBenefRows((r) => [...r, emptyRow()]);
  const benefUpdate = (i, k, v) =>
    setBenefRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const benefRemove = (i) =>
    setBenefRows((r) => (r.length === 1 ? [emptyRow()] : r.filter((_, idx) => idx !== i)));

  const dirty = useMemo(() => {
    if (Object.values(draft).some((sec) => sec && Object.values(sec).some((v) => v !== ""))) return true;
    return benefRows.some((r) => r.full_name?.trim());
  }, [draft, benefRows]);

  const save = async () => {
    if (!data || !dirty) return;
    setSaving(true);
    try {
      const payload = { ...draft };
      // Only send beneficiaries if user actually edited any row.
      const cleanRows = benefRows.filter((r) => (r.full_name || "").trim());
      if (cleanRows.length || benefRows.length === 0) {
        payload.beneficiaries = cleanRows;
      }
      const { data: result } = await api.put(`/client/portal/${token}/completion`, { payload });
      // Refresh from server result.
      setData((prev) => ({ ...prev, ...result }));
      setDraft({});
      const ben = (result.sections || []).find((s) => s.id === "beneficiaries");
      if (ben?.list_field) {
        setBenefRows(ben.items?.length ? ben.items : [emptyRow()]);
      }
      const next = (result.sections || []).find((s) => !s.complete);
      setOpenId(next ? next.id : null);
      toast.success(result.data_ready
        ? "Thank you — all details captured."
        : "Saved. Some details still needed.");
      onChanged?.(result);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="border border-zinc-200 bg-white p-6 text-zinc-500 text-sm flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading required details…
      </div>
    );
  }
  if (!data) return null;

  const completePct = data.total_sections
    ? Math.round((data.complete_sections / data.total_sections) * 100)
    : 0;
  const headerTone = data.data_ready
    ? "bg-emerald-100 text-emerald-800"
    : "bg-amber-100 text-amber-800";
  const headerLabel = data.data_ready ? "Ready for drafting" : "In progress";

  return (
    <div className="border border-zinc-200 bg-white" data-testid="completion-form">
      <div className="px-6 py-5 border-b border-zinc-200">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-overline text-zinc-500">Complete your details</div>
            <h2 className="font-serif text-2xl tracking-tight mt-1">
              We need a few more details from you
            </h2>
            <p className="mt-1 text-sm text-zinc-600 max-w-2xl">
              These details go straight into your legal document. Please make sure
              they're correct. Your conveyancer will not finalise the draft until
              everything below is captured.
            </p>
          </div>
          <div className="text-right shrink-0">
            <span className={`text-[10px] tracking-widest uppercase px-2 py-1 ${headerTone}`}
                  data-testid="completion-status-badge">
              {headerLabel}
            </span>
            <div className="text-xs text-zinc-500 mt-2" data-testid="completion-progress-text">
              {data.complete_sections} of {data.total_sections} sections complete
            </div>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] tracking-widest uppercase text-zinc-500">Progress</span>
            <span className="text-xs font-medium text-zinc-700 tabular-nums" data-testid="completion-progress-pct">
              {completePct}%
            </span>
          </div>
          <div className="h-2 bg-zinc-100 rounded-full overflow-hidden" data-testid="completion-progress-bar">
            <div
              className={`h-full transition-all duration-500 ${data.data_ready ? "bg-emerald-500" : "bg-zinc-900"}`}
              style={{ width: `${completePct}%` }}
            />
          </div>
        </div>
      </div>

      <ul className="divide-y divide-zinc-200">
        {(data.sections || []).map((section, idx) => {
          const open = openId === section.id;
          const stepNum = idx + 1;
          const actionLabel = taskActionLabel(section);
          return (
            <li key={section.id} data-testid={`completion-section-${section.id}`}>
              <button
                onClick={() => setOpenId(open ? null : section.id)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-zinc-50"
                data-testid={`section-toggle-${section.id}`}
              >
                <div className="flex items-start gap-4 min-w-0">
                  {/* Step circle */}
                  <div className={`shrink-0 mt-0.5 h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                    section.complete
                      ? "bg-emerald-600 text-white"
                      : "bg-zinc-900 text-white"
                  }`}>
                    {section.complete ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      stepNum
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-widest uppercase text-zinc-500 font-medium">
                      Step {stepNum} of {data.total_sections}
                      {section.complete && (
                        <span className="ml-2 text-emerald-700">· Complete</span>
                      )}
                    </div>
                    <div className="text-base font-medium text-zinc-900 mt-0.5">
                      {actionLabel}
                    </div>
                    {!section.complete && section.missing?.length > 0 && (
                      <div className="text-[12px] text-amber-700 mt-1">
                        Still needed: {section.missing.slice(0, 3).join(", ")}
                        {section.missing.length > 3 ? ` +${section.missing.length - 3}` : ""}
                      </div>
                    )}
                  </div>
                </div>
                {open ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
              </button>
              {open && (
                <div className="px-6 pb-6 pl-[3.75rem]">
                  {section.intro && (
                    <p className="text-xs text-zinc-500 mb-3">{section.intro}</p>
                  )}
                  {section.list_field ? (
                    <BeneficiaryGrid
                      fields={section.fields}
                      rows={benefRows}
                      onAdd={benefAdd}
                      onUpdate={benefUpdate}
                      onRemove={benefRemove}
                    />
                  ) : (
                    <FieldGrid
                      section={section}
                      values={draft[section.id] || {}}
                      onChange={(fid, v) => setField(section.id, fid, v)}
                    />
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="px-6 py-4 border-t border-zinc-200 flex items-center justify-end gap-3">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="px-5 py-2 text-sm bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          data-testid="completion-save-btn"
        >
          {saving ? "Saving…" : "Save details"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function emptyRow() {
  return { id: null, full_name: "", id_number: "", relationship: "", share_pct: "" };
}

// Convert a section's noun label ("Both spouses") into an action-oriented
// task title ("Add details for both spouses"). Keeps the experience feeling
// like a guided checklist rather than a static form.
function taskActionLabel(section) {
  const ACTION_PREFIX = {
    your_details:       "Confirm your details",
    spouses:            "Add details for both spouses",
    wedding:            "Provide your wedding details",
    executor:           "Add your executor's details",
    minors_and_guardian:"Add minor children & guardian (if any)",
    marital_status:     "Confirm your marital status",
    beneficiaries:      "List your beneficiaries",
    seller_details:     "Add the seller's details",
    transaction:        "Confirm the financial details",
    trust:              "Describe the trust",
    deceased:           "Add the deceased person's details",
  };
  return ACTION_PREFIX[section.id] || section.label;
}


function FieldGrid({ section, values, onChange }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {section.fields.map((f) => (
        <FieldInput
          key={f.id}
          field={f}
          value={values[f.id] !== undefined ? values[f.id] : (f.value ?? "")}
          onChange={(v) => onChange(f.id, v)}
          dataTestId={`field-${section.id}-${f.id}`}
        />
      ))}
    </div>
  );
}

function FieldInput({ field, value, onChange, dataTestId }) {
  const label = (
    <label className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 block mb-1">
      {field.label}
      {field.required === false && <span className="text-zinc-300 normal-case lowercase ml-1">(optional)</span>}
    </label>
  );
  if (field.type === "choice") {
    return (
      <div>
        {label}
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-zinc-200 outline-none focus:border-zinc-900 text-sm bg-white"
          data-testid={dataTestId}
        >
          <option value="">— select —</option>
          {(field.options || []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    );
  }
  if (field.type === "longtext") {
    return (
      <div className="sm:col-span-2">
        {label}
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-zinc-200 outline-none focus:border-zinc-900 text-sm resize-none"
          data-testid={dataTestId}
        />
      </div>
    );
  }
  const inputType =
    field.type === "email" ? "email" :
    field.type === "phone" ? "tel" :
    field.type === "date"  ? "date" :
    field.type === "number" ? "number" :
    "text";
  return (
    <div>
      {label}
      <input
        type={inputType}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-zinc-200 outline-none focus:border-zinc-900 text-sm"
        data-testid={dataTestId}
      />
    </div>
  );
}

function BeneficiaryGrid({ fields, rows, onAdd, onUpdate, onRemove }) {
  return (
    <div className="space-y-3" data-testid="beneficiary-grid">
      {rows.map((row, i) => (
        <div key={i} className="border border-zinc-200 p-3 grid sm:grid-cols-4 gap-2 items-end" data-testid={`beneficiary-row-${i}`}>
          {fields.map((f) => (
            <div key={f.id} className={f.id === "full_name" ? "sm:col-span-2" : ""}>
              <label className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 block mb-1">
                {f.label}
              </label>
              <input
                type={f.type === "number" ? "number" : "text"}
                value={row[f.id] || ""}
                onChange={(e) => onUpdate(i, f.id, e.target.value)}
                className="w-full px-2 py-1.5 border border-zinc-200 outline-none focus:border-zinc-900 text-sm"
                data-testid={`beneficiary-${i}-${f.id}`}
              />
            </div>
          ))}
          <button
            onClick={() => onRemove(i)}
            className="text-xs text-zinc-400 hover:text-red-700 inline-flex items-center gap-1 self-center justify-self-end"
            data-testid={`beneficiary-remove-${i}`}
          >
            <Trash2 className="h-3 w-3" /> Remove
          </button>
        </div>
      ))}
      <button
        onClick={onAdd}
        className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900 border border-dashed border-zinc-300 px-3 py-2"
        data-testid="beneficiary-add"
      >
        <Plus className="h-3 w-3" /> Add another beneficiary
      </button>
    </div>
  );
}
